import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  AppFormikSelectWithKeys,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { ITemplate } from 'modules/risk-management/risk-assessments/components/RunRiskAssessment';
import { RiskAssessmentService } from 'services/RiskAssessmentService';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Formik, FormikHelpers } from 'formik';
import { SendAssessmentVendorSchema } from 'validators/AISystemSchema';
import { ManageTeamService } from 'services/ManageTeamService';
import DoneIcon from '@mui/icons-material/Done';
import { Toast } from 'shared/helpers';
import { ITeamMember } from 'modules/settings/management/manage-team/components/types';

interface SendAssessmentProps {
  open: boolean;
  onCloseAssessment: (reload: boolean) => void;
  assessmentFor: string | undefined;
  type: 'Dataset' | 'AI';
  entityTile: string;
}
interface SendAssessmentForm {
  senderType: string;
  name: string;
  email: string;
  memberId: string;
}
const SendAssessment: React.FC<SendAssessmentProps> = (props) => {
  const { type, assessmentFor, open, onCloseAssessment, entityTile } = props;
  const [selectedTemplate, setSelectedTemplate] = useState<ITemplate>();
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [assessmentTemplates, setAssessmentTemplates] = useState<ITemplate[]>(
    []
  );
  const [sendFormValues, setSendFormValues] = useState<SendAssessmentForm>({
    senderType: 'internal',
    name: '',
    email: '',
    memberId: '',
  });
  const [teamMembers, setTeamMembers] = useState<ITeamMember[]>([]);

  const [step, setStep] = useState<
    'template' | 'select-who' | 'employee-selection' | 'vendor-form' | 'summary'
  >('template');

  // API Calls
  const loadTeamMembers = (): void => {
    const queryString = new URLSearchParams({
      offset: '0',
      limit: '1000',
    }).toString();

    ManageTeamService.teamMembers(queryString).then((response) => {
      const { user_list } = response.data.data;

      if (user_list.length > 0) {
        setTeamMembers(
          user_list.map((u: any) => ({
            ...u,
            nameEmail: `${u.name} (${u.email})`,
          }))
        );
      }
    });
  };

  console.log(teamMembers, 'TEAM MEMBERS');
  const loadAssementTemplates = (): void => {
    setIsLoadingTemplates(true);
    RiskAssessmentService.getAssessmentTemplates()
      .then((response) => {
        const { data } = response.data;
        /* eslint no-underscore-dangle: 0 */
        setAssessmentTemplates(
          data?.templates.map((d: any) => ({ ...d, id: d._id }))
        );
      })
      .catch(() => {})
      .finally(() => {
        setIsLoadingTemplates(false);
      });
  };
  const sendAssessment = (payload: any, callback: any): void => {
    RiskAssessmentService.submitRiskAssessment(payload)
      .then((response) => {
        callback(response);
      })
      .catch(() => {
        callback(false);
      });
  };

  // UI Actions
  const actionShowSelectWhom = (): void => {
    setStep('select-who');
  };

  const actionOnSubmit = (
    formValues: SendAssessmentForm,
    formhikHelpers: FormikHelpers<any>
  ): void => {
    const payload: any = {
      templateId: selectedTemplate?.id,
      type: formValues.senderType,
      aiInventoryId: type === 'AI' ? assessmentFor : null,
      datasetId: type === 'Dataset' ? assessmentFor : null,
      toUserName: null,
      toUserEmail: null,
      assessmentType: formValues.senderType,
    };
    if (formValues.senderType === 'internal') {
      const member = teamMembers.find((t) => t._id === formValues.memberId);
      payload.toUserName = member?.name;
      payload.toUserEmail = member?.email;
    }
    if (formValues.senderType === 'external') {
      payload.toUserName = formValues.name;
      payload.toUserEmail = formValues.email;
    }
    sendAssessment(payload, (response: any) => {
      formhikHelpers.setSubmitting(false);
      if (typeof response === 'object') {
        Toast.showSuccessMessage('Assessment send successfully');
        onCloseAssessment(true);
      }
    });
  };

  // Hooks
  useEffect(() => {
    loadAssementTemplates();
    loadTeamMembers();
  }, []);

  // UI Elements
  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={() => {
        onCloseAssessment(false);
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h3">Send Risk Assessment</Typography>
            <Typography variant="body1">
              Send a risk assessment for <b>{entityTile}</b>
            </Typography>
          </Box>
          <IconButton onClick={() => onCloseAssessment(false)}>
            <HighlightOffIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      {step === 'template' && (
        <>
          <DialogContent>
            {isLoadingTemplates && (
              <Box className="empty-content">
                <CircularProgress />
              </Box>
            )}
            {!isLoadingTemplates && (
              <>
                <Typography variant="h4">Select Assessment Template</Typography>
                {assessmentTemplates
                  .filter((a) => a.scope === type || a.scope === 'Both')
                  .map((t) => (
                    <Box
                      style={{
                        border: '1px solid gray',
                        cursor: 'pointer',
                        borderRadius: 10,
                        padding: '10px 20px',
                        marginBottom: 10,
                        backgroundColor:
                          t.id === selectedTemplate?.id
                            ? 'lightgray'
                            : 'transparent',
                      }}
                      key={t.id}
                      onClick={() => {
                        setSelectedTemplate(t);
                      }}
                    >
                      <Typography variant="h4">{t.templateName}</Typography>
                      <Typography>Applicability: {t.scope}</Typography>
                      <Typography>{t.description}</Typography>
                    </Box>
                  ))}
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 5 }}>
            <AppPrimaryButton
              disabled={!selectedTemplate}
              className="btn-small"
              title="Next"
              endIcon={<ArrowForwardIcon />}
              onClick={actionShowSelectWhom}
            />
          </DialogActions>
        </>
      )}

      <Formik
        initialValues={sendFormValues}
        enableReinitialize
        validationSchema={SendAssessmentVendorSchema}
        onSubmit={(formValues, formik) => {
          actionOnSubmit(formValues, formik);
        }}
      >
        {({
          values,
          isValid,
          validateForm,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <>
            {step === 'select-who' && (
              <>
                <DialogContent>
                  <Typography>Who should complete this assessment?</Typography>
                  <RadioGroup
                    className="my-5"
                    onChange={(e) => {
                      setFieldValue('senderType', e.target.value, true);
                    }}
                    value={values.senderType}
                  >
                    <Stack spacing={3}>
                      <FormControlLabel
                        value="internal"
                        control={<Radio />}
                        label={
                          <>
                            <Typography variant="body1">
                              Internal Employee
                            </Typography>
                            <Typography variant="body2">
                              Someone within your organisation
                            </Typography>
                          </>
                        }
                      />
                      <FormControlLabel
                        value="external"
                        control={<Radio />}
                        label={
                          <>
                            <Typography variant="body1">
                              External Vendor
                            </Typography>
                            <Typography variant="body2">
                              A third-party vendor or partner
                            </Typography>
                          </>
                        }
                      />
                    </Stack>
                  </RadioGroup>
                </DialogContent>
                <DialogActions sx={{ p: 5 }}>
                  <Stack
                    spacing={5}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <AppPrimaryButtonOutlined
                      className="btn-small"
                      title="Back"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => {
                        setStep('template');
                      }}
                    />
                    <AppPrimaryButton
                      className="btn-small"
                      title="Next"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => {
                        if (values.senderType === 'internal') {
                          setStep('employee-selection');
                        } else {
                          setStep('vendor-form');
                        }
                        validateForm(values);
                      }}
                    />
                  </Stack>
                </DialogActions>
              </>
            )}

            {step === 'vendor-form' && (
              <DialogContent>
                <AppFormikTextField
                  outerLabel
                  label="Vendor Contact Name *"
                  placeholder="Vendor Contact Name"
                  name="name"
                />
                <AppFormikTextField
                  outerLabel
                  label="Vendor Email *"
                  placeholder="vendor@company.com"
                  name="email"
                />
              </DialogContent>
            )}

            {step === 'employee-selection' && (
              <DialogContent>
                <AppFormikSelectWithKeys
                  name="memberId"
                  className="rework-selection"
                  notched={false}
                  labelId="fix-selection-label"
                  testid="solicitor-dropdown"
                  MenuProps={{
                    className: 'menu-fix-selection single-fix-rework',
                  }}
                  outerLabel
                  placeholder="Select User"
                  notEmphasized
                  notSelectablePlaceholder
                  label="User"
                  titleKey="nameEmail"
                  valueKey="_id"
                  items={teamMembers}
                />
              </DialogContent>
            )}

            {(step === 'employee-selection' || step === 'vendor-form') && (
              <DialogActions sx={{ p: 5 }}>
                <Stack
                  spacing={5}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <AppPrimaryButtonOutlined
                    className="btn-small"
                    title="Back"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => {
                      setStep('select-who');
                    }}
                  />
                  <AppPrimaryButton
                    className="btn-small"
                    title="Next"
                    disabled={!isValid}
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => {
                      setSendFormValues(() => ({ ...values }));
                      setStep('summary');
                    }}
                  />
                </Stack>
              </DialogActions>
            )}

            {step === 'summary' && (
              <>
                <DialogContent>
                  <Typography className="mb-2">Review & Send</Typography>
                  <Stack
                    spacing={2}
                    sx={{
                      border: '1px solid lightgray',
                      borderRadius: '5px',
                      padding: '20px',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" className="text-gray">
                        {type === 'AI' ? 'AI Solution' : 'Dataset'}
                      </Typography>
                      <Typography>{entityTile}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" className="text-gray">
                        Recipient Type:
                      </Typography>
                      <Typography>
                        {values.senderType === 'internal'
                          ? 'Internal'
                          : 'Vendor'}
                      </Typography>
                    </Box>
                    {values.senderType === 'internal' && (
                      <Box>
                        <Typography variant="body2" className="text-gray">
                          Recipient:
                        </Typography>
                        <Typography>
                          {
                            teamMembers.find((t) => t._id === values.memberId)
                              ?.nameEmail
                          }
                        </Typography>
                      </Box>
                    )}
                    {values.senderType === 'external' && (
                      <Box>
                        <Typography variant="body2" className="text-gray">
                          Vendor:
                        </Typography>
                        <Typography>
                          {values.name} ({values.email})
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="body2" className="text-gray">
                        Assessment:
                      </Typography>
                      <Typography>{selectedTemplate?.templateName}</Typography>
                    </Box>
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 5 }}>
                  <Stack
                    spacing={5}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <AppPrimaryButtonOutlined
                      className="btn-small"
                      title="Back"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => {
                        if (values.senderType === 'internal') {
                          setStep('employee-selection');
                        } else {
                          setStep('vendor-form');
                        }
                      }}
                    />
                    <AppPrimaryButton
                      className="btn-small"
                      title="Send Assessment"
                      startIcon={<DoneIcon />}
                      isLoading={isSubmitting}
                      loadingText="Sending"
                      onClick={handleSubmit}
                    />
                  </Stack>
                </DialogActions>
              </>
            )}
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default SendAssessment;
